import { useMemo } from "react";

import {
    Button,
    Column,
    FONT_SIZES,
    FONT_WEIGHTS,
    Icon,
    Image,
    InvisibleSelect,
    OverlappingImagesBase,
    Row,
    Text,
    TextButton,
    TextSkeleton
} from "@bridgesplit/ui";
import { formatDurationWithType } from "@bridgesplit/abf-sdk";
import {
    BsMetaUtil,
    formatTokenMetadata,
    MarketGuideMode,
    serializeStrategyDuration,
    usePresetMarketInfos,
    deduplicateCollateralOraclePairs
} from "@bridgesplit/abf-react";
import { AppDialog } from "app/utils";
import { DialogHeaderWithBack } from "app/components/common";

import { useQuotesContext } from "./QuotesContext";
import { useMarketCollateral, useMarketDialog } from "../util";
import { useMarketContext } from "./MarketContext";
import { ALL_COLLATERAL_VALUE } from "./constants";

export function DurationSelect() {
    const { strategyDurations, strategyDuration, setStrategyDuration } = useQuotesContext();

    return (
        <Column justifyContent="flex-start">
            <Text variant="body2" color="caption">
                Duration
            </Text>
            <InvisibleSelect
                sx={{ height: 20, justifyContent: "flex-start" }}
                renderValue={() =>
                    strategyDuration ? (
                        <Text>{formatDurationWithType(strategyDuration)}</Text>
                    ) : (
                        <TextSkeleton width="30px" variant="body1" />
                    )
                }
                value={strategyDuration ? serializeStrategyDuration(strategyDuration) : ""}
                setValue={(serializedStrategyDuration) => {
                    setStrategyDuration(
                        strategyDurations?.find((d) => serializeStrategyDuration(d) === serializedStrategyDuration)
                    );
                }}
                options={(strategyDurations ?? [])?.map((strategyDuration) => ({
                    value: serializeStrategyDuration(strategyDuration),
                    label: formatDurationWithType(strategyDuration)
                }))}
            />
        </Column>
    );
}

export function CollateralSwitch() {
    const { collateralMints, setCollateralMints } = useQuotesContext();
    const collateral = useMarketCollateral();
    const { principalMint } = useMarketContext();

    // Fetch ALL verified market infos for potential collateral
    // Don't filter by collateralMints to get all available market infos for this principal
    const { data: allVerifiedMarketInfos } = usePresetMarketInfos({
        principalMints: principalMint ? [principalMint] : [],
        addresses: []
    });

    // Deduplicate collateral pairs - picks higher LTV if same collateral in multiple market infos
    const deduplicatedResult = useMemo(() => {
        if (!allVerifiedMarketInfos) return undefined;
        return deduplicateCollateralOraclePairs(allVerifiedMarketInfos);
    }, [allVerifiedMarketInfos]);

    const selectedCollateral = useMemo(() => {
        const selected = new Set(collateralMints);
        return collateral?.filter((c) => selected.has(c.mint));
    }, [collateral, collateralMints]);

    const { open } = useMarketDialog();

    const isMaxSelected = collateral?.length === collateralMints?.length || collateralMints === ALL_COLLATERAL_VALUE;

    return (
        <Column>
            <Text color="caption" variant="body2">
                Collateral
            </Text>
            <Row spacing={0.5}>
                <Button
                    textProps={{
                        sx: { fontWeight: FONT_WEIGHTS.normal }
                    }}
                    variant="text"
                    noHover
                    height={20}
                    onClick={() =>
                        open(AppDialog.MultiSelectCollateral, {
                            tokens: collateral,
                            header: "Custom filter",
                            collateral: collateralMints,
                            principalMint,
                            isAdvanced: true,
                            collateralToMarketInfoOptions: deduplicatedResult?.collateralToMarketInfoOptions,
                            setCollateral: (collateral) => setCollateralMints(collateral.map((c) => c.metadata.mint))
                        })
                    }
                    sx={{ margin: 0, justifyContent: "flex-start", padding: 0 }}
                >
                    <>
                        {selectedCollateral ? (
                            <OverlappingImagesBase
                                size={FONT_SIZES.body1 + "px"}
                                images={selectedCollateral?.map((c) => BsMetaUtil.getImage(c)).slice(0, 3)}
                            />
                        ) : (
                            <Image variant="circle" src={undefined} size={FONT_SIZES.body1 + "px"} />
                        )}
                        {(() => {
                            if (!selectedCollateral) return <TextSkeleton width="30px" variant="body1" />;
                            if (isMaxSelected) return "All";
                            return formatTokenMetadata(selectedCollateral);
                        })()}
                    </>
                </Button>
                <Icon type="chevron-down" sx={{ height: FONT_SIZES.body1, width: FONT_SIZES.body1 }} />
            </Row>
        </Column>
    );
}

export function MarketDialogHeader({
    header,
    mode,
    back
}: {
    header: string;
    mode: MarketGuideMode;
    back?: () => void;
}) {
    const { open } = useMarketDialog();

    return (
        <DialogHeaderWithBack
            rowEnd={
                <TextButton
                    variant="h3"
                    color="disabled"
                    onClick={() => open(AppDialog.MarketGuide, { mode, isNestedDialog: true })}
                >
                    <Icon type="help" />
                </TextButton>
            }
            header={header}
            back={back}
        />
    );
}
